import { Box, Grid } from "@drone-js/core";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import { useDropzone } from "react-dropzone";
import type { UseFieldArrayReturn } from "react-hook-form";
import styled from "styled-components";
import { NotificationType } from "../../notification/constants";
import { useNotificationHandler } from "../../notification/useNotificationHandler";
import { pictureTypeMapper } from "../constants";
import { PhotoController } from "../photo/PhotoController";
import { AddPhotoController } from "./AddPhotoController";
import { UploadQueueRender } from "./UploadQueueRender";
import { useFileDropHandler } from "./useFileDropHandler";
import { useUploadPhotoHandler } from "./useUploadPhotoHandler";

interface Props extends UseFieldArrayReturn<PropertyData, "photos", "id"> {
    targetField: string;
    disableMainPhoto?: boolean;
}

const DropIndicator = styled(Box)`
    position: absolute;
    left: -1rem;
    right: -1rem;
    top: -1rem;
    bottom: -1rem;
    opacity: 30%;
`;

export const DropGrid: FunctionComponent<Props> = ({
    targetField,
    fields,
    swap,
    move,
    remove,
    append,
    disableMainPhoto,
}) => {
    const { addNotification } = useNotificationHandler();
    const { upload, queue } = useUploadPhotoHandler({
        onSuccess: (photoId, src) => {
            append({
                id: photoId,
                src,
                pictureTypeId: pictureTypeMapper.property,
            });
        },
        onError: () => addNotification(NotificationType.UploadFailed),
    });
    const { handleFileDrop } = useFileDropHandler({
        upload,
        addNotification,
    });
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept: {
            "image/jpeg": [],
            "image/png": [],
        },
        onDrop: handleFileDrop,
        noClick: true,
        noKeyboard: true,
    });

    return (
        <Box position="relative" {...getRootProps()}>
            {isDragActive && (
                <DropIndicator borderRadius="8" zIndex={100} backgroundColor="product.primary.subtle"></DropIndicator>
            )}
            <Grid>
                {fields.map((field, index) => (
                    <PhotoController
                        key={targetField + field.id}
                        index={index}
                        disableMainPhoto={disableMainPhoto}
                        targetField={targetField}
                        onMain={() => {
                            // swap between main photo and current
                            swap(0, index);
                            // move swapped 1st to the end of array
                            if (index !== 0) move(index, fields.length - 1);
                        }}
                        onRemove={() => {
                            remove(index);
                        }}
                    />
                ))}
                <UploadQueueRender queue={queue} />
                <AddPhotoController getInputProps={getInputProps} open={open} />
            </Grid>
        </Box>
    );
};
